import { Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSidebarStore } from '@/stores/sidebar-store';
import { usePluginStore } from '@/stores/plugin-store';
import { CORE_NAV_ITEMS } from '@/constants/navigation';
import { ICON_RAIL_WIDTH, PANEL_WIDTH, PANEL_SPRING } from '@/constants/layout';
import { PanelHeader } from './panel-header';
import { ContactsPanel } from '@/components/sidebar/contacts-panel';
import { EchoPanel } from '@/components/sidebar/echo-panel';
import { MarkerPanel } from '@/components/sidebar/marker-panel';
import { ChatPanel } from '@/components/sidebar/chat/chat-panel';
import { AiChatPanel } from '@/components/sidebar/chat/ai-chat-panel';
import { ToolsPanel } from '@/components/sidebar/tools-panel';
import { ReplayPanel } from '@/components/sidebar/replay-panel';
import { PluginsHubPanel } from '@/components/sidebar/plugins-hub-panel';
import { LocationsPanel } from '@/components/sidebar/locations-panel';
import { WorkspaceAdminPanel } from '@/components/sidebar/admin/workspace-admin-panel';
import { CountdownPanel } from '@/components/sidebar/countdown-panel';
import { SettingsPanel } from '@/components/sidebar/settings-panel';
import { isPluginPanel, getPluginPanelLoader, parsePluginPanelId } from '@/plugin-registry';
import vtakLogo from '@/assets/vtak-logo.png';

type PanelComponent = () => React.JSX.Element;

/** Core panel component map (always available) */
const CORE_PANELS: Record<string, PanelComponent> = {
  contacts: ContactsPanel,
  echo: EchoPanel,
  marker: MarkerPanel,
  chat: ChatPanel,
  ai: AiChatPanel,
  tools: ToolsPanel,
  replay: ReplayPanel,
  locations: LocationsPanel,
  countdown: CountdownPanel,
  admin: WorkspaceAdminPanel,
  plugins: PluginsHubPanel,
  settings: SettingsPanel,
};

/**
 * Animated slide-out panel that renders to the right of the icon rail.
 * The aside itself slides only on open/close (its key is constant); switching
 * between panels while open crossfades the inner content instead of re-sliding.
 */
export function PanelContainer(): React.JSX.Element {
  const activePanel = useSidebarStore((s) => s.activePanel);
  const setActivePanel = useSidebarStore((s) => s.setActivePanel);
  const activePlugins = usePluginStore((s) => s.activePlugins);

  const label = resolvePanelLabel(activePanel, activePlugins);
  const PanelContent = resolvePanelComponent(activePanel);

  return (
    <AnimatePresence>
      {activePanel && PanelContent && (
        <motion.aside
          key="side-panel"
          className="absolute top-0 z-[1000] flex h-full flex-col overflow-hidden bg-[#222222]/95 text-foreground shadow-[2px_0_12px_rgba(0,0,0,0.4)] backdrop-blur-sm"
          style={{ left: ICON_RAIL_WIDTH, width: PANEL_WIDTH }}
          initial={{ x: -PANEL_WIDTH }}
          animate={{ x: 0 }}
          exit={{ x: -PANEL_WIDTH }}
          transition={PANEL_SPRING}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={activePanel}
              className="flex h-full min-h-0 flex-col"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
            >
              <PanelHeader
                label={label}
                onClose={() => setActivePanel(null)}
                onBack={isPluginPanel(activePanel) ? () => setActivePanel('plugins') : undefined}
                labelContent={activePanel === 'ai' ? (
                  <img src={vtakLogo} alt="VTAK AI" className="h-5 w-auto" />
                ) : undefined}
              />
              <Suspense fallback={<PanelLoading />}>
                <PanelContent />
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

/** Resolve the panel label from core nav items or plugin config */
function resolvePanelLabel(
  panelId: string | null,
  plugins: { id: string; panels: { id: string; label: string }[] }[],
): string {
  if (!panelId) return '';
  if (panelId === 'plugins') return 'Plugins';

  const coreItem = CORE_NAV_ITEMS.find((n) => n.id === panelId);
  if (coreItem) return coreItem.label;

  const parsed = parsePluginPanelId(panelId);
  if (!parsed) return panelId;

  const plugin = plugins.find((p) => p.id === parsed.pluginId);
  const panel = plugin?.panels.find((p) => p.id === parsed.panelId);
  return panel?.label ?? panelId;
}

/** Lazy plugin-panel components, cached per panel id so re-renders never remount them */
const pluginPanelCache = new Map<string, PanelComponent>();

/** Resolve the component to render for a panel ID */
function resolvePanelComponent(panelId: string | null): PanelComponent | null {
  if (!panelId) return null;

  if (!isPluginPanel(panelId)) {
    return CORE_PANELS[panelId] ?? null;
  }

  const cached = pluginPanelCache.get(panelId);
  if (cached) return cached;

  const parsed = parsePluginPanelId(panelId);
  if (!parsed) return null;

  const loader = getPluginPanelLoader(parsed.pluginId, parsed.panelId);
  if (!loader) return null;

  const LazyPanel = lazy(loader);
  const Component: PanelComponent = () => <LazyPanel />;
  pluginPanelCache.set(panelId, Component);
  return Component;
}

/** Loading spinner shown while plugin panel components are loading */
function PanelLoading(): React.JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-center">
      <p className="text-xs text-muted-foreground">Loading...</p>
    </div>
  );
}
